import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotificationStore, useNotifications } from '../stores/notificationStore';
import { useAuth } from '../contexts/AuthContext';
import NotificationItem from '../components/notifications/NotificationItem';

const NotificationsPage: React.FC = () => {
    const { t } = useTranslation('notification');
    const navigate = useNavigate();
    const { user, isAuthenticated, isLoading: authLoading } = useAuth();

    const notifications = useNotifications();
    const {
        loading,
        hasMore,
        page,
        fetchNotifications,
        markAllAsRead,
        connect,
        disconnect,
    } = useNotificationStore();

    // Redirect if not authenticated
    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/login', { state: { from: { pathname: '/notifications' } } });
        }
    }, [authLoading, isAuthenticated, navigate]);

    // Connect WebSocket and fetch notifications
    useEffect(() => {
        if (isAuthenticated && user) {
            connect();
            fetchNotifications(1);

            return () => {
                disconnect();
            };
        }
    }, [isAuthenticated, user, connect, disconnect, fetchNotifications]);

    const handleLoadMore = () => {
        if (!loading && hasMore) {
            fetchNotifications(page + 1);
        }
    };

    const handleMarkAllRead = async () => {
        await markAllAsRead();
    };

    if (authLoading || !user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">{t('common:loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-3xl mx-auto py-8 px-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
                    {notifications.length > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                            {t('markAllAsRead')}
                        </button>
                    )}
                </div>

                {/* Notification List */}
                <div className="bg-white rounded-lg shadow">
                    {loading && notifications.length === 0 ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-4 text-gray-500">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-16 w-16 mb-4 text-gray-300"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                />
                            </svg>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {t('empty.title')}
                            </h3>
                            <p className="text-sm text-center">
                                {t('empty.description')}
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {notifications.map((notification) => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                                    onClose={() => {}}
                                />
                            ))}
                        </div>
                    )}

                    {/* Load More */}
                    {hasMore && notifications.length > 0 && (
                        <div className="px-4 py-4 border-t border-gray-100">
                            <button
                                onClick={handleLoadMore}
                                disabled={loading}
                                className="w-full py-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent mr-2" />
                                        {t('common:loading')}
                                    </span>
                                ) : (
                                    t('common:seeMore')
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationsPage;
